import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, Calendar, Info, Users, Shield, Shirt } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { isDemoTournament, getDemoTournament } from '../../data/demoData';
import { SidebarRotator } from './SidebarRotator';
import { NotificationManager } from '../features/NotificationManager';

const PublicLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { t } = useTranslation();

    // Default Theme Configuration
    const [theme, setTheme] = useState({
        color: '#4f46e5', // Default Indigo
        background: '/images/soccer crowd.webp',
        logoLeft: '',
        logoRight: '',
        logo: '' // New property
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTheme();
    }, [id]);

    const fetchTheme = async () => {
        if (isDemoTournament(id)) {
            const demoData = getDemoTournament(id);
            setTheme({
                color: demoData.theme_color,
                background: demoData.background_image || '/images/soccer crowd.webp',
                logoLeft: demoData.sponsor_left_image,
                logoRight: demoData.sponsor_right_image,
                logo: demoData.logo_url,
                sport_type: 'poule'
            });
            // Override for Esports Demo
            if (id === '87654321-4321-4321-4321-210987654321') {
                setTheme(prev => ({ ...prev, sport_type: 'esports' }));
            }
            setLoading(false);
            return;
        }

        try {
            const { data } = await supabase
                .from('tournaments')
                .select('theme_color, background_image, sponsor_left_image, sponsor_right_image, logo_url, sport_type')
                .eq('id', id)
                .single();

            if (data) {
                setTheme({
                    color: data.theme_color || '#4f46e5',
                    background: data.background_image || '/images/soccer crowd.webp',
                    logoLeft: data.sponsor_left_image,
                    logoRight: data.sponsor_right_image,
                    logo: data.logo_url,
                    sport_type: data.sport_type
                });
            }
        } catch (err) {
            console.error('Error loading theme:', err);
        } finally {
            setLoading(false);
        }
    };

    // ... helper functions ...
    const isActive = (path) => {
        if (path === '') return location.pathname === `/live/${id}`;
        return location.pathname.includes(path);
    };

    // Helper to parse images
    const parseImages = (jsonString) => {
        if (!jsonString) return [];
        try {
            if (jsonString.startsWith('[')) return JSON.parse(jsonString);
            return [jsonString];
        } catch (e) { return [jsonString]; }
    };

    const leftImages = parseImages(theme.logoLeft);
    const rightImages = parseImages(theme.logoRight);

    const isEsports = theme.sport_type === 'esports';

    // Load Cyber Font dynamically if needed
    useEffect(() => {
        if (isEsports) {
            const link = document.createElement('link');
            link.href = 'https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@500;700&display=swap';
            link.rel = 'stylesheet';
            document.head.appendChild(link);
            return () => document.head.removeChild(link);
        }
    }, [isEsports]);

    return (
        <div className={`min-h-screen w-full max-w-full overflow-x-hidden font-sans bg-fixed bg-cover bg-center transition-all duration-500 ${isEsports ? 'theme-esports text-slate-100 bg-[#030306]' : 'text-gray-800'}`} style={{ backgroundImage: `url("${theme.background}")` }}>
            <NotificationManager />
            <div className={`min-h-screen w-full max-w-full overflow-x-hidden flex flex-col items-center ${isEsports ? 'bg-black/60 backdrop-blur-sm' : 'bg-gradient-to-b from-black/20 via-black/10 to-black/60'}`}>

                {/* Top Navigation Bar */}
                <div
                    className={`w-full shadow-lg sticky top-0 z-40 border-b transition-colors duration-300 ${isEsports ? 'bg-gradient-to-r from-[#120826]/95 to-[#041e30]/95 border-purple-500/30 shadow-[0_0_25px_rgba(168,85,247,0.25)] backdrop-blur-md' : 'border-white/10'}`}
                    style={isEsports ? {} : { backgroundColor: theme.color }}
                >
                    <div className="max-w-5xl mx-auto px-4">
                        <div className="flex justify-between items-center h-16">
                            {/* Logo / Brand */}
                            <div className="flex items-center gap-3 text-white font-black text-xl italic tracking-tighter hover:opacity-90 transition-opacity cursor-pointer animate-pulse" onClick={() => navigate(`/live/${id}`)}>
                                {theme.logo ? (
                                    <div className="flex items-center gap-2">
                                        <img src={theme.logo} alt="Toernooi Logo" className="h-12 sm:h-14 w-auto max-w-[180px] sm:max-w-[250px] object-contain bg-white p-1.5 rounded-lg shadow-md transition-transform hover:scale-105" />
                                    </div>
                                ) : (
                                    <>
                                        <img src="/logo-icon.webp" alt="ProPoule" className="h-8 w-8 drop-shadow-md filter drop-shadow-[0_0_8px_rgba(217,70,239,0.5)]" />
                                        {isEsports ? (
                                            <div className="flex flex-col justify-center items-start leading-none pl-1">
                                                <span className="drop-shadow-md hidden min-[350px]:inline uppercase tracking-tight font-black text-lg italic">
                                                    PRO<span className="text-cyan-400 drop-shadow-[0_0_6px_rgba(6,182,212,0.6)]">PULSE</span>
                                                </span>
                                                <span className="text-[7px] font-black tracking-[0.22em] uppercase text-white/40 -mt-0.5 font-sans">
                                                    powered by ProPoule
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="drop-shadow-md hidden min-[350px]:inline uppercase">PRO<span className="text-yellow-300">POULE</span></span>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Desktop/Tablet Tabs */}
                            <div className="flex gap-1 md:gap-4 overflow-x-auto scrollbar-hide py-2">
                                <NavTab icon={Info} label={t('public.nav_tournament')} active={isActive('') && !isActive('matches') && !isActive('standings') && !isActive('myteam')} onClick={() => navigate(`/live/${id}`)} themeColor={theme.color} isEsports={isEsports} />
                                <NavTab icon={Shirt} label={t('public.nav_my_team')} active={isActive('myteam')} onClick={() => navigate(`/live/${id}/myteam`)} themeColor={theme.color} isEsports={isEsports} />
                                <NavTab icon={Shield} label={t('public.nav_standings')} active={isActive('standings')} onClick={() => navigate(`/live/${id}/standings`)} themeColor={theme.color} isEsports={isEsports} />
                                <NavTab icon={Calendar} label={t('public.nav_schedule')} active={isActive('matches')} onClick={() => navigate(`/live/${id}/matches`)} themeColor={theme.color} isEsports={isEsports} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Container */}
                <div className="w-full max-w-5xl flex-1 flex flex-col md:flex-row gap-6 p-4 md:p-8 animate-fade-in text-left">

                    {/* Left Sidebar (Clubs) */}
                    <div className="hidden md:flex flex-col gap-4 w-48 shrink-0">
                        {leftImages.length > 0 ? (
                            <SidebarRotator images={leftImages} type="club" />
                        ) : (
                            <div className={`backdrop-blur rounded-xl p-4 border text-center text-xs ${isEsports ? 'bg-black/40 border-purple-500/20 text-purple-300/40' : 'bg-white/5 border-white/10 text-white/30'}`}>
                                <Shield size={48} className="mx-auto mb-2 opacity-30 animate-pulse" />
                                <p>{t('public.space_clubs')}</p>
                            </div>
                        )}
                    </div>

                    {/* Central Content Card */}
                    <div className={`flex-1 min-h-[500px] rounded-2xl shadow-2xl border overflow-hidden relative flex flex-col transition-all ${isEsports ? 'bg-[#08080f]/90 border-purple-500/20 text-white shadow-[0_0_40px_rgba(168,85,247,0.1)]' : 'bg-white border-white/20'}`}>
                        {/* Top Accent Line */}
                        {isEsports ? (
                            <div className="h-1 w-full bg-gradient-to-r from-purple-600 via-fuchsia-500 to-cyan-400"></div>
                        ) : (
                            <div className="h-2 w-full" style={{ backgroundColor: theme.color }}></div>
                        )}

                        {/* Premium Partner Banner (Demo Only or Generic) */}
                        <div className={`border-b py-3 px-6 flex justify-between items-center overflow-x-auto scrollbar-hide shadow-sm relative z-10 transition-colors ${isEsports ? 'bg-[#0b0b14]/80 border-purple-500/10' : 'bg-white border-gray-100'}`}>
                            <span className={`text-[10px] font-black uppercase tracking-widest whitespace-nowrap mr-6 ${isEsports ? 'text-cyan-400/50' : 'text-indigo-900/30'}`}>
                                Official Partners
                            </span>
                            <div className={`flex gap-6 opacity-85 hover:opacity-100 transition-all duration-500 items-center ${isEsports ? 'brightness-125 contrast-125' : ''}`}>
                                {/* Hardcoded generic partners for visual pop if no dynamic ones */}
                                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Adidas_logo.png/320px-Adidas_logo.png" className="h-6 w-auto object-contain grayscale hover:grayscale-0 transition-all" alt="Adidas" onError={(e) => e.target.style.display = 'none'} />
                                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/320px-Amazon_logo.svg.png" className="h-5 w-auto object-contain grayscale hover:grayscale-0 transition-all" alt="Amazon" onError={(e) => e.target.style.display = 'none'} />
                                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/ce/Coca-Cola_logo.svg/320px-Coca-Cola_logo.svg.png" className="h-5 w-auto object-contain grayscale hover:grayscale-0 transition-all" alt="Coca-Cola" onError={(e) => e.target.style.display = 'none'} />
                            </div>
                        </div>

                        {/* Content Scroll Area */}
                        <div className={`h-full overflow-y-auto custom-scrollbar flex-1 ${isEsports ? 'bg-[#08080f] text-slate-100' : 'bg-white'}`}>
                            <Outlet context={{ isEsports, themeColor: theme.color }} />
                        </div>
                    </div>

                    {/* Right Sidebar (Sponsors) */}
                    <div className="hidden md:flex flex-col gap-4 w-48 shrink-0">
                        {rightImages.length > 0 ? (
                            <SidebarRotator images={rightImages} type="sponsor" />
                        ) : (
                            <div className={`backdrop-blur rounded-xl p-4 border text-center text-xs ${isEsports ? 'bg-black/40 border-purple-500/20 text-purple-300/40' : 'bg-white/5 border-white/10 text-white/30'}`}>
                                <Calendar size={48} className="mx-auto mb-2 opacity-30 animate-pulse" />
                                <p>{t('public.space_sponsors')}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer with Viral Loop */}
                <div className="w-full py-12 text-center flex flex-col items-center gap-3">
                    <a
                        href="/"
                        className={`glass-subtle px-6 py-3 rounded-2xl text-sm transition-all flex items-center gap-3 group shadow-lg ${isEsports ? 'border-purple-500/30 text-purple-300 hover:text-white hover:bg-purple-500/20' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                    >
                        <span className="font-medium">{t('public.footer_cta')}</span>
                        <div className="flex items-center gap-1.5 border-l border-white/20 pl-3">
                            <img src="/logo-icon.webp" alt="" className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                            <span className="font-black tracking-tighter uppercase italic text-lg">
                                <span className="text-white">PRO</span>
                                <span className="text-orange-500">POULE</span>
                            </span>
                        </div>
                    </a>
                    <div className="text-white/20 text-[10px] uppercase tracking-[0.2em] font-black">
                        &copy; {new Date().getFullYear()} — Professional Tournament Management
                    </div>
                </div>
            </div>
        </div>
    );
};

const NavTab = ({ icon: Icon, label, active, onClick, disabled, themeColor, isEsports }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={active && !isEsports ? { color: themeColor } : {}}
        className={`
            flex items-center gap-2 px-3 md:px-5 py-2 rounded-full font-bold text-xs md:text-sm tracking-wide transition-all
            ${active
                ? (isEsports 
                    ? 'bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-[0_0_15px_rgba(168,85,247,0.5)] scale-105 border border-purple-400/30' 
                    : 'bg-white shadow-lg transform scale-105')
                : (isEsports
                    ? 'text-slate-300 hover:bg-white/5 hover:text-white'
                    : 'text-white/80 hover:bg-white/20 hover:text-white')
            }
            ${disabled ? 'opacity-50 cursor-not-allowed hidden md:flex' : ''}
        `}
    >
        <Icon size={16} strokeWidth={3} className={active ? '' : ''} />
        <span>{label}</span>
    </button>
);

export default PublicLayout;
